import { useMemo } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import { useCourses } from "@/hooks";
import { ROUTES } from "@/router/routes";
import type { Course } from "@/types";
import { Table } from "@/ui";
import { getCoursesTableColumns } from "./CoursesTableColumns";

interface CoursesTableProps {
  searchQuery: string;
}

export const CoursesTable = ({ searchQuery }: CoursesTableProps) => {
  const navigate = useNavigate();
  const { useAllCourses } = useCourses();
  const { data: coursesData, isLoading } = useAllCourses();

  // Filter courses by search query
  const filteredCourses = useMemo(() => {
    return (coursesData?.data ?? []).filter(
      (course) =>
        course.name.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
    );
  }, [coursesData?.data, searchQuery]);

  const columns = useMemo(() => getCoursesTableColumns(), []);

  const table = useReactTable({
    data: filteredCourses,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (row: Course) => {
    navigate(ROUTES.editCourse.replace(":courseId", row.id));
  };

  return (
    <>
      <Table
        table={table}
        handleRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <div className="flex items-center justify-between rounded-b-2xl border border-neutral-200 px-4 py-3">
        <span className="text-sm text-neutral-500">
          {table.getRowModel().rows.length}{" "}
          {table.getRowModel().rows.length === 1 ? "course" : "courses"}
        </span>
      </div>
    </>
  );
};
